import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Star, CheckCircle, ArrowRight, GoogleLogo, FacebookLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PublicReview() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("rate"); // rate | feedback | done
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/r/${token}`);
        setData(data);
        if (data.submitted) setStep("done");
        if (data.customer_name) setContact((c) => ({ ...c, name: data.customer_name }));
      } catch (e) { toast.error(formatErr(e)); }
      setLoading(false);
    })();
  }, [token]);

  const submit = async (rt) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/r/${token}/submit`, {
        rating: rt,
        feedback: feedback || null,
        contact_name: contact.name || null,
        contact_email: contact.email || null,
        contact_phone: contact.phone || null,
      });
      setResult(data);
      setStep("done");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><span className="spinner text-zinc-400"></span></div>;
  if (!data) return <Center><h1 className="font-display text-2xl font-bold">Invalid or expired link</h1></Center>;

  const biz = data.business;
  const alreadySubmitted = step === "done" && !result;
  const submittedNow = step === "done" && !!result;

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="font-display font-black tracking-tight">{biz.name}</div>
          <div className="font-mono text-xs text-zinc-500">{biz.city}</div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Step 1: rate */}
        {step === "rate" && (
          <div className="bento p-8 md:p-12 text-center space-y-6" data-testid="public-rate">
            <div>
              <div className="label-tiny mb-3">Quick favor</div>
              <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">How was your experience with {biz.name}?</h1>
              <p className="text-zinc-600 mt-3">Tap a star to start.</p>
            </div>
            <div className="flex justify-center gap-2 py-4" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  onMouseEnter={() => setHover(s)}
                  onClick={() => {
                    setRating(s);
                    setStep("feedback");
                  }}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-${s}`}
                >
                  <Star size={56} weight={s <= (hover || rating) ? "fill" : "regular"} className={s <= (hover || rating) ? "text-[#EAB308]" : "text-zinc-300"} />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: the same review form for every rating — no review gating */}
        {step === "feedback" && (
          <div className="bento p-8 md:p-12 space-y-6" data-testid="public-feedback">
            <div>
              <div className="label-tiny mb-3">RankTrust Reviews</div>
              <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Tell people about your experience</h1>
              <p className="text-zinc-600 mt-3">Be specific and honest. Every rating follows the same RankTrust moderation process.</p>
            </div>
            <Field label="Your review">
              <textarea className="input-field" rows={4} value={feedback} onChange={(e) => setFeedback(e.target.value)} data-testid="public-feedback-text" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Your name"><input className="input-field" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="public-name" /></Field>
              <Field label="Email (optional)"><input className="input-field" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="public-email" /></Field>
              <Field label="Phone (optional)"><input className="input-field" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="public-phone" /></Field>
            </div>
            <button className="btn-primary justify-center w-full" onClick={() => submit(rating)} disabled={busy} data-testid="public-feedback-submit">
              {busy ? <span className="spinner"></span> : <>Send feedback <ArrowRight size={14} weight="bold" /></>}
            </button>
          </div>
        )}

        {/* Step 3: done */}
        {submittedNow && (
          <div className="bento p-8 md:p-12 text-center space-y-6" data-testid="public-thanks-public">
            <CheckCircle size={48} className="mx-auto text-[#22C55E]" weight="fill" />
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Thank you!</h1>
              <p className="text-zinc-600 mt-3">Your RankTrust review has been received and is pending moderation. If you also want to review {biz.name} elsewhere, the same optional links are shown to every reviewer regardless of rating.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {(result.public_options || []).map((opt) => (
                <a
                  key={opt.platform}
                  href={opt.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bento p-5 hover:border-[#002FA7] transition-colors flex flex-col items-center gap-2"
                  data-testid={`public-platform-${opt.platform}`}
                >
                  <PlatformIcon platform={opt.platform} />
                  <span className="font-display font-bold capitalize">{opt.platform}</span>
                  <span className="label-tiny text-[#002FA7]">Open <ArrowRight size={10} weight="bold" className="inline" /></span>
                </a>
              ))}
            </div>
            {(!result.public_options || result.public_options.length === 0) && (
              <p className="text-zinc-500 font-mono text-sm">The business hasn't configured public review links yet.</p>
            )}
          </div>
        )}

        {alreadySubmitted && (
          <div className="bento p-8 md:p-12 text-center" data-testid="public-already">
            <CheckCircle size={48} className="mx-auto text-zinc-400 mb-4" />
            <h1 className="font-display text-2xl font-bold">This link has already been used</h1>
            <p className="text-zinc-500 mt-2">Thanks for your feedback!</p>
          </div>
        )}
      </main>

      <footer className="py-6 text-center font-mono text-xs text-zinc-400 space-y-2">
        <div>Powered by <span className="text-zinc-600 font-semibold">RankTrust</span></div>
        <div data-testid="footer-legal-entity" className="max-w-xl mx-auto px-4">
          RankTrust is a division of Card Service Warehouse, Inc., operating under the same legal entity and EIN.
        </div>
      </footer>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block space-y-2"><span className="label-tiny block">{label}</span>{children}</label>;
}

function Center({ children }) {
  return <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6"><div className="bento p-10 text-center max-w-md w-full">{children}</div></div>;
}

function PlatformIcon({ platform }) {
  if (platform === "google") return <GoogleLogo size={28} weight="bold" className="text-[#4285F4]" />;
  if (platform === "facebook") return <FacebookLogo size={28} weight="fill" className="text-[#1877F2]" />;
  if (platform === "yelp") return <span className="font-display font-black text-2xl text-[#FF1A1A]">yelp</span>;
  return <Star size={28} weight="fill" className="text-[#EAB308]" />;
}
